/**
 * version-line.tsx — the version, where the game runs and its build, in one
 * quiet line: what a tester reads out. It is written once for every place that
 * shows it — the menu's foot, the entry's foot, the sheet a crash leaves — in
 * every environment, production included. The development build says "local"
 * and has no build.
 *
 * Only the menu's line opens a door: five presses within five seconds call
 * `onDoorFound`, which the menu answers by showing Admin. Without it the line
 * is plain text everywhere. The copyright is set in its lettering, at the
 * other end of the entry's foot (`Copyright`).
 */

import { useRef } from 'react';

import { version } from '../../version';
import styles from './version-line.module.css';

/** Presses on the line that find the door, and the time they must fall in. */
const DOOR_PRESSES = 5;
const DOOR_WINDOW_MS = 5000;

interface VersionLineProps {
  /** Called on the fifth press within five seconds — the menu's door to Admin. */
  onDoorFound?: () => void;
}

export function VersionLine({ onDoorFound }: VersionLineProps) {
  const presses = useRef<number[]>([]);
  const environment = version.environment === 'development' ? 'local' : version.environment;
  const build = version.short_sha === 'local' ? null : version.short_sha;

  const press = () => {
    const now = Date.now();
    presses.current = [...presses.current.filter((at) => at > now - DOOR_WINDOW_MS), now];
    if (presses.current.length >= DOOR_PRESSES) {
      presses.current = [];
      onDoorFound?.();
    }
  };

  return (
    <p className={styles.line} onClick={onDoorFound ? press : undefined}>
      <span className={styles.version}>{version.semantic_version}</span>
      {[environment, build]
        .filter(Boolean)
        .map((part) => ` · ${part}`)
        .join('')}
    </p>
  );
}

/** The copyright, in the version line's lettering. */
export function Copyright() {
  return <p className={styles.line}>© {new Date().getFullYear()} Ponder Inc.</p>;
}
